import React, { useState, useCallback } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { UserManager } from '../../../APIManager'
import MultiSelectWithCheckBox, { type MultiSelectWithCheckBoxProps } from '../../../components/FormElements/MultiSelectWithCheckBox'

interface User {
  userId: string
  name: string
  email: string
}

interface UserRoleMultiSelectProps extends Omit<MultiSelectWithCheckBoxProps<User>, 'getOptionLabel' | 'getOptionValue' | 'loading' | 'onChange' | 'options'> {
  roleName: string
  name: string
  label?: string
  placeholder?: string
}

const UserRoleMultiSelect: React.FC<UserRoleMultiSelectProps> = ({
  roleName,
  name,
  label,
  placeholder,
  ...props
}) => {
  const [input, setInput] = useState<string>('')

  const { data: searchData, isLoading: isSearchLoading, isFetching: isSearchFetching } = useQuery<User[]>({
    queryKey: ['usersSummary', input],
    queryFn: async (): Promise<User[]> => {
      const params: Record<string, any> = {
        pageSize: 10,
        search: input,
        is_faculty: true
      }
      const response = await UserManager.getUsersSummaryApi(params)
      return response.data
    },
    enabled: true,
    placeholderData: keepPreviousData
  })

  const handleInputChange = useCallback((text: string) => {
    setInput(text)
  }, [])

  const handleOptionSelected = useCallback(() => {
    setInput('')
  }, [])

  const options = Array.isArray(searchData) ? searchData : []
  const isLoading = isSearchLoading || isSearchFetching
  const hasOptions = Array.isArray(options) && options.length > 0

  return (
    <MultiSelectWithCheckBox
      label={label}
      name={name}
      options={Array.isArray(options) ? options : []}
      placeholder={hasOptions ? placeholder : 'Start typing to search users...'}
      getOptionLabel={(option: User) => option?.name || ''}
      getOptionValue={(option: User) => option?.userId || ''}
      getOptionSubLabel={(option: User) => option?.email || ''}
      loading={isLoading}
      fullWidth
      onInputChange={handleInputChange}
      onOptionSelected={handleOptionSelected}
      showSelectAll
      renderTagsType="chips"
      {...props}
    />
  )
}

export default UserRoleMultiSelect
